import { useCallback, useMemo, useReducer } from 'react';

import { AtlBrowserStorageLocal } from '@atlassian/browser-storage-controls/web-storage-service';
import {
	getSideNavDefaultCollapsedLocalStorageKey,
	getSideNavSidebarWidthLocalStorageKey,
	getSideNavUserMenuDataLocalStorageKey,
} from '@atlassian/navigation-common/side-nav-storage';

import { useAnalyticsEvents } from '../../../../common/analytics/generated/use-analytics-events';
import type { Store, UserMenuData, UserSideNavData } from '../../../../common/types';
import type { LocalStorageState } from './types';

function readStoredValue(key: string): string | null {
	if (typeof window === 'undefined') {
		return null;
	}
	try {
		return AtlBrowserStorageLocal.getItem(key);
	} catch {
		// Storage may be unavailable, for example when the browser denies access.
		return null;
	}
}

function parseWidth(raw: string | null): number | undefined {
	if (!raw?.trim()) {
		return undefined;
	}
	const width = Number(raw);
	return Number.isFinite(width) && width > 0 ? width : undefined;
}

function parseUserMenuData(raw: string | null): UserMenuData {
	try {
		const data: unknown = JSON.parse(raw ?? 'null');
		if (
			Array.isArray(data) &&
			data.every(
				(item) =>
					item !== null &&
					typeof item === 'object' &&
					typeof item.menuId === 'string' &&
					typeof item.visible === 'boolean',
			)
		) {
			return data;
		}
	} catch {
		// Invalid data in one key must not discard the other preferences.
	}
	return [];
}

async function writeValue(key: string, value: string | undefined): Promise<void> {
	if (value === undefined) {
		AtlBrowserStorageLocal.removeItem(key);
		return;
	}
	const result = await AtlBrowserStorageLocal.setItem(key, value);
	if (result.status === 'ERROR') {
		throw result.error;
	}
	if (result.status !== 'SUCCESS') {
		throw new Error(`Failed to write side nav preference: ${result.status}`);
	}
}

/**
 * Gate-on local persistence. The legacy combined key is deliberately untouched.
 * Keep GSN's synchronous initial reads: ADS captures defaultCollapsed/defaultWidth
 * on mount. Deferring reads until after hydration would lose saved preferences.
 * Like GTN, this does not subscribe to cross-tab changes; ADS owns live visibility.
 */
export function useSplitLocalSideNavUserData({ app }: { app: string }): Store {
	const collapsedKey = getSideNavDefaultCollapsedLocalStorageKey(app);
	const widthKey = getSideNavSidebarWidthLocalStorageKey(app);
	const menuKey = getSideNavUserMenuDataLocalStorageKey(app);
	const collapsed = readStoredValue(collapsedKey);
	const width = readStoredValue(widthKey);
	const menu = readStoredValue(menuKey);
	const userMenuData = useMemo(() => parseUserMenuData(menu), [menu]);
	const { fireEvent } = useAnalyticsEvents();
	const [, refresh] = useReducer((version: number) => version + 1, 0);

	const persist = useCallback(
		async ({ userSideNavData, userMenuData }: Partial<LocalStorageState>) => {
			try {
				const writes: Promise<void>[] = [];
				// Write only supplied fields: resizing or changing menus must not overwrite
				// a collapsed preference written by GTN since this store last rendered.
				if (userSideNavData && 'defaultCollapsed' in userSideNavData) {
					writes.push(writeValue(collapsedKey, userSideNavData.defaultCollapsed?.toString()));
				}
				if (userSideNavData && 'defaultWidth' in userSideNavData) {
					writes.push(writeValue(widthKey, userSideNavData.defaultWidth?.toString()));
				}
				if (userMenuData !== undefined) {
					writes.push(writeValue(menuKey, JSON.stringify(userMenuData)));
				}
				await Promise.all(writes);
				refresh();
				fireEvent('operational.executeMutation.succeeded', { storage: 'local' });
			} catch (error) {
				fireEvent('operational.executeMutation.failed', {
					storage: 'local',
					error: error instanceof Error ? error.message : String(error),
				});
			}
		},
		[collapsedKey, widthKey, menuKey, fireEvent],
	);

	const setUserMenuData = useCallback(
		(userMenuData: UserMenuData) => {
			void persist({ userMenuData });
		},
		[persist],
	);
	const setUserSideNavData = useCallback(
		(userSideNavData: UserSideNavData) => {
			void persist({ userSideNavData });
		},
		[persist],
	);

	return {
		userSideNavData: {
			defaultCollapsed: collapsed === 'true' ? true : collapsed === 'false' ? false : undefined,
			defaultWidth: parseWidth(width),
		},
		userMenuData,
		setUserMenuData,
		setUserSideNavData,
	};
}
